import moment from "moment";
import {fileUtils} from "../utils/file_utils";
import {API_DOMAIN} from "../utils/constants";
import {parseInt} from "lodash";
import {Capacitor} from "@capacitor/core";

const DB_NAME = 'dotag', DB_VERSION = 61;
let DB, idb;

idb = window.indexedDB;

export default {

  async getDb() {
    return new Promise((resolve, reject) => {

      if (DB) {
        return resolve(DB);
      }

      let request = idb.open(DB_NAME, DB_VERSION);


      request.onerror = e => {
        console.log('Error opening db', e);
        alert('Failed to open database');
        reject('Error opening db');
      };

      let storeExample;

      request.onsuccess = e => {
        DB = e.target.result;

        // Once the browser starts closing this connection (e.g. after the WebView
        // reclaims it under memory pressure while backgrounded), every future
        // transaction on it throws InvalidStateError forever - reset the cache so
        // the next getDb() call transparently opens a fresh connection instead.
        DB.onversionchange = () => {
          DB.close();
          DB = null;
        };
        DB.onclose = () => {
          DB = null;
        };

        if (storeExample) {

          this.saveFolder({
            id: 1,
            folder_id: null,
            name: 'Example Worksite',
            parent_id: null,
            project_id: null
          }).then(() => {

            fileUtils.createProjectFileDirectory(1);
          }).then(() => {
            fileUtils.getBlobFromUrl(API_DOMAIN + '/example/example-drawing.pdf').then(blob => {
              this.saveFile({
                id: 1,
                file_id: 1,
                folder_id: 1,
                name: 'Example drawing.dotag',
                marking_count: 0,
                project_id: null,
                size: null,
                type: 'pdf',
                updated_at: moment().toISOString(),
                content: blob
              });
            });
          }).then(() => {
            fileUtils.getBlobFromUrl(API_DOMAIN + '/example/example-drawing.jpeg').then(blob => {
              this.savePdfPage({
                id: 1,
                file_id: 1,
                height: 3306,
                width: 4678,
                page: 1,
                content: blob
              });
            }).then(() => {
              resolve(DB);
            });
          });

        } else {
          resolve(DB);
        }
      };

      request.onupgradeneeded = async e => {

        console.log('UPGRADING DB');

        let db = e.target.result;

        if (!db.objectStoreNames.contains("users")) {
          db.createObjectStore("users", {autoIncrement: true, keyPath: 'id'});
        }

        if (!db.objectStoreNames.contains("settings")) {
          db.createObjectStore("settings", {autoIncrement: true, keyPath: 'id'});
        }

        if (!db.objectStoreNames.contains("projects")) {
          const projects = db.createObjectStore("projects", {autoIncrement: false, keyPath: 'id'});
          projects.createIndex("company_id", "company_id", {unique: false});
          projects.createIndex("is_synced", "is_synced", {unique: false});
        }

        if (!db.objectStoreNames.contains("companies")) {
          const companies = db.createObjectStore("companies", {autoIncrement: false, keyPath: 'id'});
          companies.createIndex("is_synced", "is_synced", {unique: false});
        }

        if (!db.objectStoreNames.contains("folders")) {
          const folders = db.createObjectStore("folders", {autoIncrement: true, keyPath: 'id'});
          folders.createIndex("project_id", "project_id", {unique: false});
          folders.createIndex("is_synced", "is_synced", {unique: false});

          storeExample = true;
        }

        if (!db.objectStoreNames.contains("files")) {
          const files = db.createObjectStore("files", {autoIncrement: true, keyPath: 'id'});
          files.createIndex("project_id", "project_id", {unique: false});
          files.createIndex("folder_id", "folder_id", {unique: false});
          files.createIndex("is_synced", "is_synced", {unique: false});

        }

        if (!db.objectStoreNames.contains("pdf_pages")) {
          const pdf_pages = db.createObjectStore("pdf_pages", {autoIncrement: true, keyPath: 'id'});
          pdf_pages.createIndex("file_id", "file_id", {unique: false});
        }

        if (!db.objectStoreNames.contains("markings")) {
          db.createObjectStore("markings", {autoIncrement: true, keyPath: 'id'});
        }

        // Collaborate mode: markings downloaded from a teammate for offline editing.
        // collaborate CRuD functions never touch the "markings" store.
        if (!db.objectStoreNames.contains("collaborate_markings")) {
          const collaborateMarkings = db.createObjectStore("collaborate_markings", {autoIncrement: true, keyPath: 'id'});
          collaborateMarkings.createIndex("file_id", "file_id", {unique: false});
        }

        if (!db.objectStoreNames.contains("collaborate_teammates_cache")) {
          db.createObjectStore("collaborate_teammates_cache", {keyPath: 'file_id'});
        }

        if (!db.objectStoreNames.contains("reports")) {
          db.createObjectStore("reports", {autoIncrement: true, keyPath: 'id'});
        }

        if (!db.objectStoreNames.contains("project_tools")) {
          const tools = db.createObjectStore("project_tools", {autoIncrement: true, keyPath: 'id'});
          tools.createIndex("project_id", "project_id", {unique: false});
          tools.createIndex("company_id", "company_id", {unique: false});
        }

        if (DB_VERSION < 43) {
          if (Capacitor.getPlatform() !== 'web') {
            const newStorageDirectory = fileUtils.getStorageDirectory();
            const newFilesDirectory = (fileUtils.getStorageDirectory() + '/files/').replace('//files', '/files');
            const oldFilesDirectory = (cordova.file.externalApplicationStorageDirectory + '/files/').replace('//files', '/files');

            let nativeFilesDirectory = await fileUtils.getNativeURL(newFilesDirectory);

            nativeFilesDirectory = nativeFilesDirectory.endsWith('/') ? nativeFilesDirectory.slice(0, -1) : nativeFilesDirectory;

            await this.moveFiles(nativeFilesDirectory);
            await this.movePages(nativeFilesDirectory);
            await this.moveMarkingAttachments(nativeFilesDirectory);

            if (oldFilesDirectory !== newFilesDirectory) {
              await fileUtils.moveFolder(oldFilesDirectory, newStorageDirectory, 'files', false);
            }
          }
        }

        if (DB_VERSION < 45) {
          const deviceId = typeof device !== 'undefined' ? device.uuid : 'testing';
          await this.addDeviceIdToMarkings(deviceId);
        }

        if (DB_VERSION < 100) {
          await this.addMarkingIndexObjectToFiles();
        }

        if (DB_VERSION < 56) {
          await this.addDeletedAtToMarkings();
        }

        if (DB_VERSION < 57) {
          await this.addIsDirtyToMarkings();
        }

        if (DB_VERSION < 59) {
          await this.setIsDownloadedForExistingFiles();
        }

      };
    });
  },

  async addDeletedAtToMarkings() {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('markings');

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          let marking = cursor.value;
          if (typeof marking.deleted_at === 'undefined') {
            marking.deleted_at = null;
            cursor.update(marking);
          }
          cursor.continue();
        }
      };

    });
  },

  async addIsDirtyToMarkings() {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('markings');

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          let marking = cursor.value;
          if (typeof marking.is_dirty === 'undefined') {
            marking.is_dirty = true;
            cursor.update(marking);
          }
          cursor.continue();
        }
      };

    });
  },

  async setIsDownloadedForExistingFiles() {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('files');

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          let file = cursor.value;
          if (typeof file.is_downloaded === 'undefined') {
            file.is_downloaded = true;
            cursor.update(file);
          }
          cursor.continue();
        }
      };

    });
  },

  async addMarkingIndexObjectToFiles() {
    return new Promise((resolve, reject) => {
      this.getFiles().then((files) => {
        for (const file of files) {
          if (typeof file.markingTypeIndexes !== 'undefined') {
            continue;
          }


          this.getMarkings(file.id).then(async (markings) => {
            let markingTypeIndexes = {};

            for (const marking of markings) {

              const markingIndexKey = marking.type + marking.markingPrefix;

              if (typeof markingTypeIndexes[markingIndexKey] === 'undefined' || markingTypeIndexes[markingIndexKey] < marking.markingIndex) {
                markingTypeIndexes[markingIndexKey] = parseInt(marking.markingIndex);
              }
            }

            file.markingTypeIndexes = markingTypeIndexes;

            await this.saveFile(file);
          }).then(() => {
            resolve();
          });


        }
      });
    });
  },

  async moveFiles(directory) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');

      store.openCursor().onsuccess = async e => {
        let cursor = e.target.result;
        if (cursor) {
          let file = cursor.value;
          if (file.url) {
            file.url = directory + '/' + file.id + '/' + file.url.substring(file.url.lastIndexOf('/') + 1);
            cursor.update(file);
          }
          cursor.continue();
        }
      };

    });
  },
  async movePages(directory) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['pdf_pages'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('pdf_pages');

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          let page = cursor.value;
          if (page.url) {
            page.url = directory + '/' + page.file_id + '/' + page.url.substring(page.url.lastIndexOf('/') + 1);

            cursor.update(page);
          }

          cursor.continue();
        }
      };

    });
  },

  async moveMarkingAttachments(directory) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('markings');

      store.openCursor().onsuccess = async e => {
        let cursor = e.target.result;
        if (cursor) {
          let marking = cursor.value;

          let markingChanged = false;

          for (let i = 0; i < marking.attachments.length; i++) {

            if (marking.attachments[i].url) {

              const newFilePath = directory + '/' + marking.file_id + '/' + marking.attachments[i].url.substring(marking.attachments[i].url.lastIndexOf('/') + 1);
              marking.attachments[i].url = newFilePath;

              markingChanged = true;
            }
          }

          if (markingChanged) {
            cursor.update(marking);
          }

          cursor.continue();
        }
      };

    });
  },

  async addDeviceIdToMarkings(deviceId) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('markings');

      store.openCursor().onsuccess = async e => {
        let cursor = e.target.result;
        if (cursor) {
          let marking = cursor.value;
          marking.device_id = deviceId;

          cursor.update(marking);
          cursor.continue();
        }
      };

    });
  },


  async getUser() {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['users'], 'readonly');
      trans.oncomplete = () => {
        if (users.length > 0) {
          resolve(users[0]);
        } else {
          resolve(null);
        }
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('users');
      let users = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          users.push(cursor.value);
          cursor.continue();
        }
      };

    });
  },
  async saveUser(user) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['users'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('users');
      store.put(user);

    });
  },
  async getProjects() {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['projects'], 'readonly');
      trans.oncomplete = () => {
        if (projects.length > 0) {
          resolve(projects);
        } else {
          resolve(null);
        }
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('projects');
      let projects = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          projects.push(cursor.value);
          cursor.continue();
        }
      };

    });
  },
  async getProject(projectID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      if (!projectID) {
        console.log('invalid project id', projectID);
        reject();
      }

      let trans = db.transaction(['projects'], 'readonly');

      trans.oncomplete = () => {
        resolve(project);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('projects');
      let project = {};
      let request = store.get(Number(projectID));

      request.onsuccess = function (e) {
        project = e.target.result;
      };

    });
  },
  async getFolders(projectID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['folders'], 'readonly');

      trans.oncomplete = () => {
        resolve(folders);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('folders');
      let folders = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {

          if (!projectID || projectID === cursor.value.project_id) {
            folders.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },
  async getFoldersByParentID(parentID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['folders'], 'readonly');

      trans.oncomplete = () => {
        resolve(folders);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('folders');
      let folders = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {

          if (typeof cursor.value.parent_id === 'undefined' || parentID === cursor.value.parent_id) {
            folders.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },
  async getFolder(folderID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['folders'], 'readonly');

      trans.oncomplete = () => {
        resolve(folder);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('folders');
      let folder = null;

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {

          if (folderID === cursor.value.folder_id) {
            folder = cursor.value;
          }
          cursor.continue();
        }
      };

    });
  },
  async saveFolder(folder) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['folders'], 'readwrite');

      trans.oncomplete = () => {
        folder.id = request.result;
        resolve(folder);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('folders');
      let request = store.put(folder);
    });
  },
  async getFiles(fileTypes) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readonly');
      trans.oncomplete = () => {
        resolve(files);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');
      let files = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (fileTypes === 'fast') {
            if (cursor.value.is_fast) {
              files.push(cursor.value);
            }
          } else if (!cursor.value.is_fast) {
            files.push(cursor.value);
          }


          cursor.continue();
        }
      };

    });
  },
  async getProjectFiles(projectID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readonly');
      trans.oncomplete = () => {
        resolve(files);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');
      let files = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (cursor.value.project_id === projectID) {
            files.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },
  async getFilesByFolderID(folderID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readonly');

      trans.oncomplete = () => {
        resolve(files);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');
      let files = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (cursor.value.folder_id === folderID) {
            files.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },
  async getFile(fileID) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {
      let trans = db.transaction(['files'], 'readonly');

      trans.oncomplete = () => {
        resolve(file);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');
      let file = {};

      let request = store.get(Number(fileID));

      request.onsuccess = function (e) {
        file = e.target.result;
      };
    });
  },


  async getFileByFileID(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readonly');
      let request;
      let file;

      trans.oncomplete = () => {
        resolve(file);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');

      request = store.openCursor();

      request.onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (Number(cursor.value.file_id) === Number(fileID)) {
            file = cursor.value;
          }
          cursor.continue();
        }
      };

    });
  },
  async deleteFile(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');

      store.delete(fileID);

    });
  },
  async saveFile(file) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['files'], 'readwrite');

      trans.oncomplete = () => {
        file.id = request.result;
        resolve(file);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('files');
      let request = store.put(file);
    });
  },
  async getMarkings(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let cursorRequest;
      let markings = [];
      let trans = db.transaction(['markings'], 'readonly');

      trans.oncomplete = () => {
        resolve(markings);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('markings');

      cursorRequest = store.openCursor();

      cursorRequest.onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (Number(cursor.value.file_id) === Number(fileID)) {
            markings.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },
  async deleteFileMarkings(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('markings');

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (Number(fileID) === Number(cursor.value.file_id)) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    });
  },
  async replaceFileMarkings(fileID, newMarkings) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('markings');

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (Number(fileID) === Number(cursor.value.file_id)) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          for (const marking of newMarkings) {
            store.put(marking);
          }
        }
      };
    });
  },
  async saveMarking(marking) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('markings');
      store.put(marking);
    });
  },
  async deleteMarking(markingID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('markings');

      store.delete(markingID);

    });
  },

  // --- Collaborate mode markings do not merge with standard markings.
  async getCollaborateMarkings(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let markings = [];
      let trans = db.transaction(['collaborate_markings'], 'readonly');

      trans.oncomplete = () => {
        resolve(markings);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('collaborate_markings');
      let index = store.index('file_id');

      index.openCursor(IDBKeyRange.only(Number(fileID))).onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          markings.push(cursor.value);
          cursor.continue();
        }
      };
    });
  },
  async saveCollaborateMarking(marking) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['collaborate_markings'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('collaborate_markings');
      store.put(marking);
    });
  },
  async deleteCollaborateMarking(markingID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['collaborate_markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('collaborate_markings');

      store.delete(markingID);

    });
  },
  async deleteFileCollaborateMarkings(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['collaborate_markings'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('collaborate_markings');
      let index = store.index('file_id');

      index.openCursor(IDBKeyRange.only(Number(fileID))).onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
    });
  },
  async getTeammatesCache(fileID) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['collaborate_teammates_cache'], 'readonly');

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('collaborate_teammates_cache');
      let request = store.get(Number(fileID));

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });
  },
  async saveTeammatesCache(fileID, teammates) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['collaborate_teammates_cache'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('collaborate_teammates_cache');
      store.put({file_id: Number(fileID), teammates, fetched_at: new Date().toISOString()});
    });
  },
  async deleteTeammatesCache(fileID) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['collaborate_teammates_cache'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
      };

      let store = trans.objectStore('collaborate_teammates_cache');
      store.delete(Number(fileID));
    });
  },

  async getPdfPages(fileID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let cursorRequest;
      let pdf_pages = [];
      let trans = db.transaction(['pdf_pages'], 'readonly');

      trans.oncomplete = () => {
        resolve(pdf_pages);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);

        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('pdf_pages');

      cursorRequest = store.openCursor();

      cursorRequest.onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (Number(cursor.value.file_id) === Number(fileID)) {
            pdf_pages.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },
  async savePdfPage(pdfPage) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      this.deletePdfPages(pdfPage.file_id, pdfPage.page).then(function () {

        let trans = db.transaction(['pdf_pages'], 'readwrite');

        trans.oncomplete = () => {
          resolve();
        };

        trans.onerror = (event) => {
          console.log(event, trans.error);
          reject(trans.error);
          alert('DB operation failed. Please try again.');
        };

        let store = trans.objectStore('pdf_pages');
        store.put(pdfPage);

      });
    });
  },
  async deletePdfPages(fileID, pageNr) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['pdf_pages'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('pdf_pages');

      let cursorRequest = store.openCursor();

      cursorRequest.onsuccess = async e => {
        let cursor = e.target.result;
        if (cursor) {
          if (fileID && fileID === cursor.value.file_id) {
            if (pageNr) {
              if (pageNr === cursor.value.page) {
                await store.delete(cursor.value.id);
              }
            } else {
              await store.delete(cursor.value.id);
            }
          }
          cursor.continue();
        } else {
          resolve('Deleted');
        }
      };

    });
  },

  async saveCompanies(companies) {

    await this.clearCompanies();

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['companies'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('companies');

      for (let index in companies) {
        companies[index].is_synced = 1;
        store.put(companies[index]);
      }
    });
  },

  async clearCompanies() {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {
      let trans = db.transaction(['companies'], 'readwrite');


      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('companies');
      store.clear();

    });
  },

  async saveProjects(projects) {

    let db = await this.getDb();
    let dbProjects = await this.getProjects();

    for (let dbProjectsIndex in dbProjects) {
      let found = false;

      for (let pIndex in projects) {

        if (projects[pIndex].id === dbProjects[dbProjectsIndex].id) {
          found = true;
          projects[pIndex].last_updated = typeof dbProjects[dbProjectsIndex].last_updated !== 'undefined' ? dbProjects[dbProjectsIndex].last_updated : null;
          projects[pIndex].is_synced = 1;
        }
      }

      if (!found) {
        await this.deleteProject(dbProjects[dbProjectsIndex].id);
      }


    }

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['projects'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('projects');

      for (let index in projects) {
        store.put(projects[index]);
      }
    });

  },

  async deleteProject(projectID) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['projects'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('projects');

      store.delete(projectID);

    });
  },

  async clearProjects() {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {
      let trans = db.transaction(['projects'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('projects');
      store.clear();

    });
  },

  async updateProject(project) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['projects'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      if (typeof project.files !== 'undefined') {
        delete project.files;
      }

      let store = trans.objectStore('projects');
      store.put(project);

    });
  },

  async saveReport(report) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['reports'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        alert('DB operation failed. Please try again.');
        reject(trans.error);
      };

      let store = trans.objectStore('reports');

      store.put(report);

    });
  },
  async getReports() {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['reports'], 'readonly');
      trans.oncomplete = () => {
        if (reports.length > 0) {
          resolve(reports);
        } else {
          resolve(null);
        }
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('reports');
      let reports = [];

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          reports.push(cursor.value);
          cursor.continue();
        }
      };

    });
  },
  async deleteReport(reportID) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['reports'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('reports');

      store.delete(reportID);

    });
  },

  async getArchivedReport(reportID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['reports'], 'readonly');

      trans.oncomplete = () => {
        resolve(report);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('reports');
      let report = {};

      let request = store.get(Number(reportID));

      request.onsuccess = function (e) {
        report = e.target.result;
      };

    });
  },

  async clearDB() {

    let db = await this.getDb();

    return new Promise((resolve) => {


      let trans = db.transaction(['users', 'companies', 'projects', 'settings'], 'readwrite');
      trans.objectStore('users').clear();
      trans.objectStore('companies').clear();
      // trans.objectStore('files').clear();
      // trans.objectStore('folders').clear();
      // trans.objectStore('markings').clear();
      // trans.objectStore('pdf_pages').clear();
      // trans.objectStore('projects').clear();
      // trans.objectStore('reports').clear();
      // trans.objectStore('project_tools').clear();
      trans.objectStore('settings').clear();

      resolve();


    });
  },

  async getProjectTools(projectID) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['project_tools'], 'readonly');
      let tools = [];

      trans.oncomplete = () => {
        resolve(tools);
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('project_tools');


      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (cursor.value.project_id === projectID) {
            tools.push(cursor.value);
          }
          cursor.continue();
        }
      };

    });
  },

  async saveProjectTools(tools) {

    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      if (tools.length > 0) {
        let trans = db.transaction(['project_tools'], 'readwrite');

        trans.oncomplete = () => {
          resolve();
        };

        trans.onerror = (event) => {
          console.log(event, trans.error);
          reject(trans.error);
          alert('DB operation failed. Please try again.');
        };

        let store = trans.objectStore('project_tools');

        for (let index in tools) {
          store.put(tools[index]);
        }
      } else {
        resolve();
      }

    });
  },

  async deleteProjectTools(projectID) {
    let db = await this.getDb();

    return new Promise((resolve, reject) => {

      let trans = db.transaction(['project_tools'], 'readwrite');

      trans.oncomplete = () => {
        resolve();
      };

      trans.onerror = (event) => {
        console.log(event, trans.error);
        reject(trans.error);
        alert('DB operation failed. Please try again.');
      };

      let store = trans.objectStore('project_tools');

      let cursorRequest = store.openCursor();

      cursorRequest.onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (projectID && projectID === cursor.value.project_id) {
            store.delete(cursor.value.id);
          }
          cursor.continue();
        } else {
          resolve('Deleted');
        }
      };

    });
  },
};
